// app/auth/register/page.tsx
"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

// Account Flow Components
import RegisterStep1 from "@/components/onboarding/RegisterStep1";
import ProgressBar from "@/components/onboarding/progressbar";
import AccountRegisterStep2 from "@/components/onboarding/registration/AccountRegisterStep2";
import AccountRegisterStep4 from "@/components/onboarding/registration/AccountRegisterStep4";
import AccountRegisterStep3 from "@/components/onboarding/registration/AccountRegisterStep3";

// Consultation Flow Components
import ConsultStep2 from "@/components/onboarding/consultation/ConsultStep2";
import ConsultStep3 from "@/components/onboarding/consultation/ConsultStep3";
import ConsultStep4 from "@/components/onboarding/consultation/ConsultStep4";
import ConsultStep5 from "@/components/onboarding/consultation/ConsultStep5";
import ConsultStep6 from "@/components/onboarding/consultation/ConsultStep6";
import { RegisterFormData, registerSchema } from "@/lib/schemas/authSchema";
import { toast } from "sonner";
import { ConsultFormData, consultSchema } from "@/lib/schemas/consultSchema";
import api from "@/lib/axiosInstance";
import AuthSidePanel from "@/components/onboarding/AuthSidePanel";

export default function RegisterPage() {
  const [step, setStep] = useState<number>(1);
  const router = useRouter();
  const [flowType, setFlowType] = useState<"consultation" | "account">(
    "consultation"
  );
  const [formData, setFormData] = useState<Partial<RegisterFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = flowType === "consultation" ? 6 : 4;

  // --- Navigation ---
  const goToNext = (data: any) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const goToPrevious = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  // --- Account Flow: Submit Final Data ---
  const createAccount = async (data: Partial<RegisterFormData>) => {
    const finalData = { ...formData, ...data } as RegisterFormData;

    // Validate with Zod
    const result = registerSchema.safeParse(finalData);
    if (!result.success) {
      const errorMsg = result.error.issues[0]?.message || "Invalid data";
      toast.error(errorMsg);
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedData = {
        ...result.data,
        role: result.data.role === "homeowner" ? "user" : result.data.role,
      };

      await api.post("/auth/register", normalizedData);

      toast.success("Account created! Check your email to verify.");
      setFormData({ ...finalData });
      setStep(4); // Go to verify email step
    } catch (error: any) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong during registration.";
      const authProvider = error.response?.data?.authProvider;
      
      // Check if user tried to register with email but already has Google account
      if (authProvider === "GOOGLE") {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:5000/api';
        toast.error(error.response?.data?.msg || msg, {
          duration: 6000,
          action: {
            label: "Sign in with Google",
            onClick: () => {
              window.location.href = `${backendUrl}/auth/google/login`;
            },
          },
        });
      } else {
        toast.error(error.response.data.msg || msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Consultation Flow: Final Submit ---
  const handleSubmitConsultation = async (data: any) => {
    const finalData = { ...formData, ...data } as Partial<ConsultFormData>;

    // Validate with Zod
    const result = consultSchema.safeParse(finalData);
    if (!result.success) {
      const errorMsg =
        result.error.issues[0]?.message || "Invalid consultation data";
      toast.error(errorMsg);
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await api.post("/consultation", result.data);

      toast.success("Thank you! Our team will contact you soon.");
      setFormData(finalData);
      setStep(6); // Show success screen
    } catch (error: any) {
      const msg =
        error.response?.data?.msg ||
        "Failed to submit consultation. Please check your connection.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step rendering logic moved to a separate function
  const renderStep = () => {
    if (step === 1) {
      return (
        <RegisterStep1
          onNext={(data) => {
            setFlowType(data.flowType);
            setFormData((prev: any) => ({
              ...prev,
              intent: data.intent,
              flowType: data.flowType,
            }));
            setStep(2);
          }}
        />
      );
    }
    // Consultation Flow
    if (flowType === "consultation") {
      switch (step) {
        case 2:
          return <ConsultStep2 onBack={goToPrevious} onNext={goToNext} />;
        case 3:
          return <ConsultStep3 onBack={goToPrevious} onNext={goToNext} />;
        case 4:
          return <ConsultStep4 onBack={goToPrevious} onNext={goToNext} />;
        case 5:
          return (
            <ConsultStep5
              onBack={goToPrevious}
              onSubmit={handleSubmitConsultation}
              isSubmitting={isSubmitting}
              defaultValues={formData}
            />
          );
        case 6:
          return <ConsultStep6 onExplore={() => router.push("/marketplace")} />;
        default:
          return <RegisterStep1 onNext={() => {}} />;
      }
    }

    // Account Flow
    if (flowType === "account") {
      switch (step) {
        case 2:
          return (
            <AccountRegisterStep2
              onBack={goToPrevious}
              onNext={(role) => goToNext({ role })}
            />
          );
        case 3:
          return (
            <AccountRegisterStep3
              onBack={goToPrevious}
              onSubmit={createAccount}
              defaultValues={formData}
              isSubmitting={isSubmitting}
            />
          );
        case 4:
          return (
            <AccountRegisterStep4
              onBack={goToPrevious}
              email={formData.email}
            />
          );
        default:
          return <RegisterStep1 onNext={() => {}} />;
      }
    }

    return <RegisterStep1 onNext={() => {}} />;
  };

  return (
    <div className="flex h-full min-h-screen w-full bg-white">
      <AuthSidePanel
        headline="Get Started"
        subline="Join Nigeria's leading solar marketplace. Connect with verified manufacturers, distributors, and recyclers."
        features={["Request quotes from verified providers", "Track your solar journey", "Access exclusive deals"]}
      />

      {/* Right Side - Step Content */}
      <div className="flex w-full h-full md:w-2/3 flex-col justify-center items-center px-8 py-10 lg:px-16">
        {/* Progress Bar */}
        <ProgressBar currentStep={step} totalSteps={totalSteps} />

        {/* Step Content */}
        <div className="w-full max-w-xl mx-auto h-full">{renderStep()}</div>
      </div>
    </div>
  );
}
